import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { HTTP } from 'utils/http';
import * as Sentry from '@sentry/react';
import { resolveOverlayError, resolveUserError } from 'utils/userErrorMessages';
import { trackPurchase, purchaseItem, purchaseCustomer } from 'utils/tracking';
import { safeLocalStorage, safeSessionStorage } from 'utils/safeStorage';
import type {
  AlmaPaymentState,
  AlmaPaymentStatusResponse,
  AlmaRollbackResponse,
} from 'types/alma';

const RETRY_DELAYS = [2000, 4000, 8000]; // 2s, 4s, 8s
const PROCESSING_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const SLOW_NETWORK_THRESHOLD = 5000; // 5 secondes

export function useAlmaPaymentProcessor() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Utiliser useRef pour éviter les re-créations de fonctions
  const isProcessingRef = useRef(false);

  const [state, setState] = useState<AlmaPaymentState>({
    isProcessing: false,
    currentStep: 'verifying',
    currentAttempt: 0,
    maxAttempts: 3,
    error: null,
    errorStep: null,
    isSlowNetwork: false,
    paymentId: null,
  });

  // ========================================================================
  // GESTION DU LOCALSTORAGE POUR ÉVITER LES DOUBLES TRAITEMENTS
  // ========================================================================

  const markPaymentAsProcessing = useCallback((paymentId: string) => {
    const processingKey = `processingAlmaPayment_${paymentId}`;
    const timestampKey = `processingAlmaTimestamp_${paymentId}`;

    safeLocalStorage.setItem(processingKey, 'true');
    safeLocalStorage.setItem(timestampKey, Date.now().toString());
  }, []);

  const isPaymentProcessing = useCallback((paymentId: string) => {
    const processingKey = `processingAlmaPayment_${paymentId}`;
    const timestampKey = `processingAlmaTimestamp_${paymentId}`;

    const isProcessing = safeLocalStorage.getItem(processingKey) === 'true';
    const timestamp = safeLocalStorage.getItem(timestampKey);

    if (isProcessing && timestamp) {
      const processingTime = Date.now() - parseInt(timestamp);

      // Si le traitement dure plus de 10 minutes, considérer comme expiré
      if (processingTime > PROCESSING_TIMEOUT) {
        safeLocalStorage.removeItem(processingKey);
        safeLocalStorage.removeItem(timestampKey);
        return false;
      }
      return true;
    }

    return false;
  }, []);

  const clearProcessingFlags = useCallback((paymentId: string) => {
    const processingKey = `processingAlmaPayment_${paymentId}`;
    const timestampKey = `processingAlmaTimestamp_${paymentId}`;

    safeLocalStorage.removeItem(processingKey);
    safeLocalStorage.removeItem(timestampKey);
  }, []);

  // ========================================================================
  // ROLLBACK ALMA (REMBOURSEMENT)
  // ========================================================================

  const rollbackAlmaPayment = useCallback(
    async (paymentId: string) => {
      try {
        // Vérifier si le service a déjà été crédité avant de rembourser
        const canRollback = await HTTP.get<{ canRollback: boolean; reason?: string }>(
          '/sale/canrollbackalmapayment',
          { pid: paymentId }
        ).send();

        if (!canRollback.canRollback) {
          toast({
            title: 'Paiement déjà traité',
            description: 'Votre achat a bien été crédité sur votre compte. Aucun remboursement nécessaire.',
            status: 'success',
            duration: 8000,
            isClosable: true,
          });
          return false;
        }

        const cancelResponse = await HTTP.get<AlmaRollbackResponse>('/sale/rollbackalmapayment', {
          pid: paymentId,
        }).send();

        const refund = cancelResponse.refunds?.find((r) => r.payment === paymentId);

        if (!refund || !refund.id) {
          const { title, description } = resolveUserError('refund');
          toast({
            title,
            description,
            status: 'error',
            duration: 8000,
            isClosable: true,
          });
          return false;
        }

        toast({
          title: 'Paiement annulé',
          description: 'Votre paiement a été automatiquement remboursé.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });

        return true;
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setTag('where', 'useAlmaPaymentProcessor:rollbackAlmaPayment');
          scope.setExtra('paymentId', paymentId);
          Sentry.captureException(error);
        });

        const { title, description } = resolveUserError('refund', error);
        toast({
          title,
          description,
          status: 'error',
          duration: 8000,
          isClosable: true,
        });

        return false;
      }
    },
    [toast]
  );

  // ========================================================================
  // VÉRIFICATION DU STATUT DU PAIEMENT ALMA
  // ========================================================================

  const checkAlmaPaymentStatus = useCallback(async (paymentId: string): Promise<boolean> => {
    const startTime = Date.now();

    try {
      // Créer un timeout de 30 secondes pour la vérification du paiement Alma
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout after 30 seconds')), 30000);
      });

      // Utiliser keepalive pour que la requête survive aux navigations
      const requestPromise = HTTP.get<AlmaPaymentStatusResponse>(
        '/sale/checkalmapaymentstatus',
        { pid: paymentId },
        { keepalive: true }
      ).send();

      const response = await Promise.race([requestPromise, timeoutPromise]);

      const duration = Date.now() - startTime;

      // Détecter une connexion lente
      if (duration > SLOW_NETWORK_THRESHOLD) {
        setState((prev) => ({ ...prev, isSlowNetwork: true }));
      }

      return response?.success === true;
    } catch (error) {
      const duration = Date.now() - startTime;

      // Si la requête a pris du temps ET a échoué, c'est probablement un problème réseau
      if (duration > SLOW_NETWORK_THRESHOLD) {
        setState((prev) => ({ ...prev, isSlowNetwork: true }));
      }

      Sentry.withScope((scope) => {
        scope.setTag('where', 'useAlmaPaymentProcessor:checkAlmaPaymentStatus');
        scope.setLevel('error');
        scope.setExtra('paymentId', paymentId);
        scope.setExtra('duration', duration);
        scope.setExtra('errorMessage', error instanceof Error ? error.message : 'Unknown error');
        Sentry.captureException(error);
      });

      throw error;
    }
  }, []);

  // ========================================================================
  // TRAITEMENT DU SERVICE APRÈS SUCCÈS ALMA
  // ========================================================================

  const processServicePurchase = useCallback(async (paymentId: string) => {
    const paymentData = safeSessionStorage.getItem('paymentData');

    if (!paymentData) {
      const error = new Error('Payment data not found in session storage');

      Sentry.withScope((scope) => {
        scope.setTag('where', 'useAlmaPaymentProcessor:processServicePurchase');
        scope.setLevel('error');
        scope.setExtra('paymentId', paymentId);
        Sentry.captureException(error);
      });

      throw error;
    }

    const parsedPaymentData = JSON.parse(paymentData);

    // 🔒 SÉCURITÉ : Vérifier que le paymentId correspond bien à celui stocké dans AlmaPaymentId
    // Cela protège contre les tentatives de manipulation du paymentId
    if (parsedPaymentData.AlmaPaymentId && parsedPaymentData.AlmaPaymentId !== paymentId) {
      const error = new Error('Payment ID mismatch - potential security issue');

      Sentry.withScope((scope) => {
        scope.setTag('where', 'useAlmaPaymentProcessor:processServicePurchase');
        scope.setLevel('error');
        scope.setExtra('expectedPaymentId', parsedPaymentData.AlmaPaymentId);
        scope.setExtra('receivedPaymentId', paymentId);
        Sentry.captureException(error);
      });

      throw error;
    }

    // 🔒 SÉCURITÉ : S'assurer que AlmaPaymentId est bien présent dans les données envoyées
    // Le backend va vérifier ce champ pour empêcher les replay attacks
    if (!parsedPaymentData.AlmaPaymentId) {
      Sentry.captureMessage('AlmaPaymentId missing in session data - fallback to URL parameter', {
        level: 'warning',
        tags: {
          where: 'useAlmaPaymentProcessor:processServicePurchase',
          paymentMethod: 'alma',
        },
        extra: {
          paymentId,
          hasSessionData: !!paymentData,
        },
      });
      parsedPaymentData.AlmaPaymentId = paymentId;
    }

    const response = await HTTP.post('/sale/services', {
      ServiceId: parsedPaymentData.ServiceId,
      Payment: parsedPaymentData,
      Test: false,
    }).send();

    safeSessionStorage.setItem('paymentId', paymentId);

    return response;
  }, []);

  // ========================================================================
  // FONCTION PRINCIPALE DE TRAITEMENT AVEC RETRY
  // ========================================================================

  const processAlmaPayment = useCallback(
    async (paymentId: string) => {
      // Protection contre les appels multiples
      if (isProcessingRef.current) {
        Sentry.addBreadcrumb({
          category: 'alma',
          message: 'Alma processing skipped: already in progress (ref)',
          data: { paymentId },
        });
        return;
      }

      // Vérifier si déjà en cours de traitement (autre onglet/session)
      if (isPaymentProcessing(paymentId)) {
        Sentry.addBreadcrumb({
          category: 'alma',
          message: 'Alma processing skipped: localStorage processing flag set',
          data: { paymentId },
        });
        return;
      }

      // Vérifier si déjà traité avec succès
      const tempPaymentId = safeSessionStorage.getItem('paymentId');
      if (tempPaymentId === paymentId) {
        Sentry.addBreadcrumb({
          category: 'alma',
          message: 'Alma processing skipped: already processed in this session',
          data: { paymentId },
        });

        // Purger le pid en attente : si le client re-visite le lien de retour
        // Alma après un succès, main.tsx ré-écrit alma_pending_payment et sans
        // cette purge chaque pageload suivant re-déclenchait ce guard — boucle
        // de redirections vers « Mes séances » pendant 10 min (vu le 04/06 sur
        // riise-annecy, 6 × pid_recovered_from_storage).
        safeLocalStorage.removeItem('alma_pending_payment');
        safeLocalStorage.removeItem('alma_pending_timestamp');

        navigate('/account/services?alma_success=1', { replace: true });
        return;
      }

      // Signal de début de traitement du retour : permet de rapprocher
      // return_intercepted ↔ processing_started ↔ credited dans Sentry et de
      // repérer les retours qui ne déclenchent jamais la créditation.
      Sentry.captureMessage('Alma return processing started', {
        level: 'info',
        tags: {
          category: 'payment',
          paymentMethod: 'alma',
          alma_step: 'processing_started',
        },
        extra: { paymentId },
      });

      // Marquer comme en cours
      isProcessingRef.current = true;
      markPaymentAsProcessing(paymentId);
      setState({
        isProcessing: true,
        currentStep: 'verifying',
        currentAttempt: 0,
        maxAttempts: 3,
        error: null,
        errorStep: null,
        isSlowNetwork: false,
        paymentId,
      });

      // Attendre que le document soit complètement chargé
      if (document.readyState !== 'complete') {
        await new Promise((resolve) => {
          window.addEventListener('load', resolve, { once: true });
        });
      }

      // Attendre 3 secondes supplémentaires pour laisser le temps à Alma de traiter
      await new Promise((resolve) => setTimeout(resolve, 3000));

      let attempt = 0;
      while (attempt < RETRY_DELAYS.length) {
        try {
          setState((prev) => ({ ...prev, currentAttempt: attempt + 1 }));

          // Vérifier le statut du paiement
          const isPaymentValid = await checkAlmaPaymentStatus(paymentId);

          if (isPaymentValid) {
            // Passer à l'étape de traitement (paiement validé)
            setState((prev) => ({ ...prev, currentStep: 'processing' }));

            // Petite pause pour l'animation (500ms)
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Passer à l'étape de créditation Mindbody
            setState((prev) => ({ ...prev, currentStep: 'crediting' }));

            try {
              // Traiter le service - ÉTAPE CRITIQUE
              await processServicePurchase(paymentId);

              // Invalider les caches pour forcer le rechargement des services
              // (+ achats et abonnements : le serveur vient de staler le miroir membre)
              await queryClient.invalidateQueries({ queryKey: ['/client/services'] });
              await queryClient.invalidateQueries({ queryKey: ['/client/purchases'] });
              await queryClient.invalidateQueries({ queryKey: ['/client/clientcontracts'] });
              await queryClient.refetchQueries({ queryKey: ['/client/services'] });

              // Passer à l'étape de succès
              setState((prev) => ({ ...prev, currentStep: 'success' }));
            } catch (creditError: any) {
              // Erreur spécifique lors de la créditation Mindbody
              Sentry.withScope((scope) => {
                scope.setTag('where', 'useAlmaPaymentProcessor:processAlmaPayment:crediting');
                scope.setTag('error_type', 'mindbody_crediting_failed');
                scope.setLevel('error');
                scope.setExtra('paymentId', paymentId);
                scope.setExtra('errorMessage', creditError.message);
                scope.setExtra('errorDetails', creditError);
                Sentry.captureException(creditError);
              });

              throw new Error(`Mindbody crediting failed: ${creditError.message}`);
            }

            // Signal de succès de bout en bout côté front
            Sentry.captureMessage('Alma payment credited (front flow complete)', {
              level: 'info',
              tags: {
                category: 'payment',
                paymentMethod: 'alma',
                alma_step: 'credited_front',
              },
              extra: { paymentId },
            });

            // GA4 purchase : paiement Alma crédité avec succès au retour.
            // Le contexte produit (montant, service, promo) a été stocké en
            // session par CheckoutForm avant la redirection vers Alma.
            try {
              const stored = safeSessionStorage.getItem('paymentData');
              const pd = stored ? JSON.parse(stored) : {};
              trackPurchase({
                transactionId: paymentId,
                value: Number(pd.TotalAmount ?? pd.Amount) || 0,
                items: [purchaseItem(pd.ServiceId, Number(pd.TotalAmount ?? pd.Amount) || 0)],
                paymentType: 'Alma',
                coupon: pd.PromoCode || undefined,
                customer: purchaseCustomer({ email: pd.Email, phone: pd.Phone }),
              });
            } catch {
              // jamais bloquant pour le crédit du paiement
            }

            // Attendre 2 secondes pour l'animation
            await new Promise((resolve) => setTimeout(resolve, 2000));

            // Succès - nettoyer et rediriger
            clearProcessingFlags(paymentId);
            isProcessingRef.current = false;
            setState((prev) => ({ ...prev, isProcessing: false }));

            // Nettoyer le localStorage
            safeLocalStorage.removeItem('alma_pending_payment');
            safeLocalStorage.removeItem('alma_pending_timestamp');

            navigate('/account/services?alma_success=1', { replace: true });
            return;
          } else {
            throw new Error('Payment not validated by Alma');
          }
        } catch (error: any) {

          // Si c'est la dernière tentative
          if (attempt === RETRY_DELAYS.length - 1) {
            // Déterminer à quelle étape l'erreur s'est produite
            const currentErrorStep = state.currentStep;
            const overlayStep =
              currentErrorStep === 'crediting'
                ? 'crediting'
                : currentErrorStep === 'verifying' || currentErrorStep === 'processing'
                  ? 'processing'
                  : 'unknown';
            const errorMessage = resolveOverlayError(overlayStep, error.message);

            setState((prev) => ({
              ...prev,
              currentStep: 'rolling_back',
              error: errorMessage,
              errorStep: currentErrorStep,
            }));

            // Log Sentry de l'échec final avec détails
            Sentry.withScope((scope) => {
              scope.setTag('where', 'useAlmaPaymentProcessor:processAlmaPayment');
              scope.setTag('error_type', 'final_failure');
              scope.setTag('error_step', currentErrorStep);
              scope.setLevel('error');
              scope.setExtra('paymentId', paymentId);
              scope.setExtra('totalAttempts', attempt + 1);
              scope.setExtra('errorMessage', error.message);
              scope.setExtra('failedAtStep', currentErrorStep);
              Sentry.captureException(error);
            });

            // Effectuer le rollback
            await rollbackAlmaPayment(paymentId);

            // Passer à l'étape d'échec
            setState((prev) => ({ ...prev, currentStep: 'failed' }));

            // Nettoyer les flags mais GARDER isProcessing = true pour afficher la modale
            clearProcessingFlags(paymentId);
            isProcessingRef.current = false;

            // Nettoyer le localStorage
            safeLocalStorage.removeItem('alma_pending_payment');
            safeLocalStorage.removeItem('alma_pending_timestamp');

            // NE PAS rediriger automatiquement - l'utilisateur doit cliquer sur "Fermer"
            // La modale reste ouverte avec isProcessing: true
            return;
          }

          // Attendre avant la prochaine tentative
          const delay = RETRY_DELAYS[attempt];
          await new Promise((resolve) => setTimeout(resolve, delay));
        }

        attempt++;
      }
    },
    [
      isPaymentProcessing,
      markPaymentAsProcessing,
      checkAlmaPaymentStatus,
      processServicePurchase,
      clearProcessingFlags,
      rollbackAlmaPayment,
      navigate,
      toast,
      queryClient,
    ]
  );

  // ========================================================================
  // ANNULATION MANUELLE
  // ========================================================================

  const cancelPayment = useCallback(
    async (paymentId: string) => {
      setState((prev) => ({ ...prev, currentStep: 'rolling_back' }));

      await rollbackAlmaPayment(paymentId);

      clearProcessingFlags(paymentId);
      isProcessingRef.current = false;
      setState((prev) => ({ ...prev, isProcessing: false }));

      // Nettoyer le localStorage
      safeLocalStorage.removeItem('alma_pending_payment');
      safeLocalStorage.removeItem('alma_pending_timestamp');

      navigate('/account/services', { replace: true });
    },
    [clearProcessingFlags, rollbackAlmaPayment, navigate]
  );

  return {
    state,
    processAlmaPayment,
    cancelPayment,
    isPaymentProcessing,
  };
}
